/**
 * Zod schemas for the cross-tenant admin API (`/v1/admin/*`).
 *
 * Route and client agree by construction: `apps/saas-api` validates with these
 * and `apps/admin` infers its types from them, which is the repo convention.
 * Responses stay inside the usual `{ success, data | error }` envelope, so what
 * is described here is the `data` payload only.
 *
 * Only the parts the plan states outright are modelled. Where the plan is
 * silent (diagnostics, usage reconciliation) nothing is guessed: a wrong schema
 * is more expensive than a missing one, because both sides would build against
 * it before anyone noticed.
 */

import { z } from "zod";
import {
	ACTIVITY_ACTOR_TYPES,
	ACTIVITY_EVENT_ACTIONS,
	ADMIN_AUDIT_ACTIONS,
	ADMIN_AUDIT_TARGET_TYPES,
	PLATFORM_ROLES,
} from "../constants/admin.ts";

// ============ Common ============

const FLAG_TRUE = ["1", "true", "yes", "on"];
const FLAG_FALSE = ["0", "false", "no", "off"];

/** `undefined` for anything that is not a recognised flag spelling. */
function readFlag(raw: unknown): boolean | undefined {
	if (typeof raw === "boolean") return raw;
	if (typeof raw !== "string") return undefined;
	const value = raw.trim().toLowerCase();
	if (FLAG_TRUE.includes(value)) return true;
	if (FLAG_FALSE.includes(value)) return false;
	return undefined;
}

/**
 * A boolean that survives the trip through a query string.
 *
 * Deliberately not `z.coerce.boolean()`: that is JavaScript truthiness, so
 * `?stuckOnly=false` arrives as `true`. It shipped exactly that way — the
 * browser-sessions table sends the flag explicitly on every request, so the list
 * silently filtered to "stuck only" and showed an empty table while 45 sessions
 * existed. `apps/local-api` learned the same lesson from `VEIL_AUTO_CLOSE=false`
 * switching the feature on; see `booleanEnv` in its config.
 *
 * A real boolean passes through, because the typed client sends JSON. An
 * unrecognised spelling falls back to the default rather than reading as truthy,
 * so a typo cannot silently invert a filter.
 */
const flag = (fallback: boolean) =>
	z
		.union([z.boolean(), z.string()])
		.optional()
		.transform((raw) => readFlag(raw) ?? fallback);

/** The same, with no default: absent or unrecognised means "do not filter". */
const optionalFlag = () =>
	z.union([z.boolean(), z.string()]).optional().transform(readFlag);

export const PlatformRoleSchema = z
	.enum(PLATFORM_ROLES)
	.describe("VeilBrowser staff role");

/**
 * Shared shape for every admin list endpoint. Admin lists span every tenant, so
 * paging, sorting and filtering are always the server's job; there is no
 * "fetch everything and filter in the browser" variant of these.
 *
 * The page/limit pair restates `PaginationQuerySchema` rather than extending
 * it. `schemas/index.ts` re-exports this file, so importing back from it would
 * make the module graph circular and have `.extend()` run against a
 * half-initialised binding — a failure that only shows up depending on which
 * entry point a consumer imports first. The cap is 200 rather than the tenant
 * default of 2000 because these queries scan every tenant.
 */
export const AdminListQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1).describe("Page number"),
	limit: z.coerce
		.number()
		.int()
		.min(1)
		.max(200)
		.default(50)
		.describe("Items per page"),
	sortOrder: z.enum(["asc", "desc"]).default("desc").describe("Sort direction"),
	search: z
		.string()
		.trim()
		.max(200)
		.optional()
		.describe("Free-text search across the endpoint's indexed columns"),
});

export type AdminListQuery = z.infer<typeof AdminListQuerySchema>;

/**
 * Route params. Restated here rather than imported from `schemas/index.ts` for
 * the same reason `AdminListQuerySchema` restates the pagination pair: that file
 * re-exports this one, so importing back would make the module graph circular.
 */
export const AdminIdParamsSchema = z.object({
	id: z.string().min(1).describe("Resource id"),
});

export const AdminOrgMemberParamsSchema = z.object({
	id: z.string().min(1).describe("Organization id"),
	memberId: z.string().min(1).describe("Member id"),
});

/**
 * A destructive operator action needs a stated reason, because the audit row is
 * only useful if it says why. Kept as its own schema so every such route asks
 * for it the same way.
 */
export const AdminReasonSchema = z.object({
	reason: z
		.string()
		.trim()
		.min(1)
		.max(500)
		.describe("Why, for the audit trail"),
});

export type AdminReason = z.infer<typeof AdminReasonSchema>;

// ============ Users ============

export const AdminUsersQuerySchema = AdminListQuerySchema.extend({
	sortBy: z
		.enum(["createdAt", "email", "name", "role"])
		.default("createdAt")
		.describe("Sort column"),
	role: PlatformRoleSchema.optional().describe("Filter by platform role"),
	banned: optionalFlag().describe("Filter by ban state"),
	twoFactorEnabled: optionalFlag().describe(
		"Filter by second-factor enrolment",
	),
}).describe("Cross-tenant user list query");

export type AdminUsersQuery = z.infer<typeof AdminUsersQuerySchema>;

export const AdminUserSchema = z
	.object({
		id: z.string(),
		name: z.string().nullable(),
		email: z.string(),
		emailVerified: z.boolean(),
		image: z.string().nullable(),
		/**
		 * Null for a customer. The admin plugin's `defaultRole` is `user`, which
		 * is normalised to null here so the panel has one "not staff" value.
		 */
		role: PlatformRoleSchema.nullable(),
		banned: z.boolean(),
		banReason: z.string().nullable(),
		banExpires: z.string().nullable().describe("ISO 8601, null when permanent"),
		twoFactorEnabled: z.boolean(),
		organizationCount: z.number().int().min(0),
		createdAt: z.string(),
		updatedAt: z.string(),
	})
	.describe("A user as the operator console sees it");

export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminSetRoleSchema = z
	.object({
		/** Null demotes an operator back to a plain customer account. */
		role: PlatformRoleSchema.nullable(),
	})
	.describe("Promote or demote an operator");

export type AdminSetRole = z.infer<typeof AdminSetRoleSchema>;

export const AdminBanUserSchema = z
	.object({
		reason: z.string().trim().min(1).max(500),
		/** Seconds from now. Omitted means the ban does not expire. */
		expiresIn: z.number().int().positive().optional(),
	})
	.describe("Ban a user");

export type AdminBanUser = z.infer<typeof AdminBanUserSchema>;

export const AdminSetPasswordSchema = z
	.object({
		password: z.string().min(12).max(200),
	})
	.describe("Set a user's password");

export type AdminSetPassword = z.infer<typeof AdminSetPasswordSchema>;

// ============ Organizations ============

export const AdminOrganizationsQuerySchema = AdminListQuerySchema.extend({
	sortBy: z
		.enum(["createdAt", "name", "slug"])
		.default("createdAt")
		.describe("Sort column"),
	planSlug: z.string().trim().max(100).optional(),
}).describe("Cross-tenant organization list query");

export type AdminOrganizationsQuery = z.infer<
	typeof AdminOrganizationsQuerySchema
>;

export const AdminOrganizationSchema = z
	.object({
		id: z.string(),
		name: z.string(),
		slug: z.string(),
		logo: z.string().nullable(),
		memberCount: z.number().int().min(0),
		profileCount: z.number().int().min(0),
		planSlug: z.string().nullable(),
		subscriptionStatus: z.string().nullable(),
		createdAt: z.string(),
	})
	.describe("An organization as the operator console lists it");

export type AdminOrganization = z.infer<typeof AdminOrganizationSchema>;

export const AdminUpdateOrganizationSchema = z
	.object({
		name: z.string().trim().min(1).max(200).optional(),
		slug: z
			.string()
			.trim()
			.regex(/^[a-z0-9][a-z0-9-]{0,62}$/)
			.optional(),
		metadata: z.record(z.string(), z.unknown()).nullable().optional(),
	})
	.describe("Edit an organization");

export type AdminUpdateOrganization = z.infer<
	typeof AdminUpdateOrganizationSchema
>;

/**
 * Fixing a membership from the console. `owner` is absent: promoting someone to
 * owner is a transfer, not a role change, because an organization has exactly one
 * and the old one has to go somewhere. That is `AdminTransferOwnershipSchema`.
 */
export const AdminUpdateMemberRoleSchema = z
	.object({
		role: z.enum(["admin", "member"]),
		reason: z.string().trim().min(1).max(500),
	})
	.describe("Change a member's role inside their organization");

export type AdminUpdateMemberRole = z.infer<typeof AdminUpdateMemberRoleSchema>;

export const AdminTransferOwnershipSchema = z
	.object({
		/** The member row to promote, not the user id: a user may be in many orgs. */
		memberId: z.string().min(1),
		reason: z.string().trim().min(1).max(500),
	})
	.describe("Move ownership of an organization to another member");

export type AdminTransferOwnership = z.infer<
	typeof AdminTransferOwnershipSchema
>;

// ============ Plans ============

/**
 * Plan writes. `Plan` is seed-only today, which the plan calls the single
 * largest missing capability, so this is a full create/update body rather than
 * a patch of a couple of fields.
 *
 * `apiRpmLimit` / `apiRphLimit` are stored and returned but nothing enforces
 * them yet. The panel must label them advisory rather than implying a ceiling
 * that does not exist.
 */
/**
 * `Plan.type` is a Postgres enum, so this is closed rather than a free string —
 * a plan created with an unknown type would fail on the column, and a 400 that
 * lists the options beats a 500 that does not.
 */
export const PLAN_TYPES = [
	"lite",
	"starter",
	"base",
	"team",
	"advanced",
	"custom",
] as const;

export const PlanTypeSchema = z.enum(PLAN_TYPES).describe("Plan tier");

export type PlanTypeValue = (typeof PLAN_TYPES)[number];

export const AdminPlanInputSchema = z
	.object({
		name: z.string().trim().min(1).max(100),
		slug: z
			.string()
			.trim()
			.regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
		type: PlanTypeSchema,
		description: z.string().trim().max(1000).nullable().optional(),
		priceMonthly: z.number().int().min(0).nullable(),
		priceYearly: z.number().int().min(0).nullable(),
		stripePriceIdMonthly: z.string().trim().max(200).nullable().optional(),
		stripePriceIdYearly: z.string().trim().max(200).nullable().optional(),
		profileLimit: z.number().int().min(0),
		memberLimit: z.number().int().min(0),
		apiRpmLimit: z.number().int().min(0).nullable().optional(),
		apiRphLimit: z.number().int().min(0).nullable().optional(),
		additionalProfilePrice: z.number().int().min(0).nullable().optional(),
		additionalMemberPrice: z.number().int().min(0).nullable().optional(),
		additionalRpmPrice: z.number().int().min(0).nullable().optional(),
		features: z.record(z.string(), z.unknown()).nullable().optional(),
		isActive: z.boolean().default(true),
		isCustom: z.boolean().default(false),
		sortOrder: z.number().int().default(0),
	})
	.describe("Create or replace a plan");

export type AdminPlanInput = z.infer<typeof AdminPlanInputSchema>;

export const AdminPlanUpdateSchema =
	AdminPlanInputSchema.partial().describe("Patch a plan");

export type AdminPlanUpdate = z.infer<typeof AdminPlanUpdateSchema>;

export const AdminPlansQuerySchema = z
	.object({
		/**
		 * Defaults to every plan, unlike the public `GET /v1/plans`. An operator
		 * managing the catalogue needs to see the archived ones.
		 */
		includeInactive: flag(true),
	})
	.describe("Plan catalogue query");

export type AdminPlansQuery = z.infer<typeof AdminPlansQuerySchema>;

/**
 * How many subscriptions reference a plan.
 *
 * Returned by every plan write, because editing a plan does **not** change what
 * existing subscribers get: `Subscription` snapshots limits and pricing at
 * creation, on purpose, so a customer keeps what they signed up for. That is
 * invisible in a form that just says "saved", so the count comes back with the
 * response and the console is expected to say so plainly.
 */
export const AdminPlanSubscriberCountSchema = z.object({
	total: z.number().int().min(0).describe("Subscriptions ever created on it"),
	active: z
		.number()
		.int()
		.min(0)
		.describe("Subscriptions currently active or trialing"),
});

export type AdminPlanSubscriberCount = z.infer<
	typeof AdminPlanSubscriberCountSchema
>;

// ============ Subscriptions ============

/**
 * A manual grant. Every field is optional because the four things an operator
 * does here — move a plan, adjust add-ons, extend a trial, cancel — are
 * independent, and `reason` is required so the audit entry says why.
 */
export const AdminSubscriptionOverrideSchema = z
	.object({
		planSlug: z.string().trim().max(100).optional(),
		additionalProfiles: z.number().int().min(0).optional(),
		additionalMembers: z.number().int().min(0).optional(),
		additionalRpm: z.number().int().min(0).optional(),
		trialEndsAt: z.string().datetime().nullable().optional(),
		currentPeriodEnd: z.string().datetime().optional(),
		cancelAtPeriodEnd: z.boolean().optional(),
		reason: z.string().trim().min(1).max(500),
	})
	.describe("Manual subscription override");

export type AdminSubscriptionOverride = z.infer<
	typeof AdminSubscriptionOverrideSchema
>;

export const AdminSubscriptionsQuerySchema = AdminListQuerySchema.extend({
	sortBy: z.enum(["createdAt", "status"]).default("createdAt"),
	status: z.string().trim().max(50).optional(),
}).describe("Subscription history query");

export type AdminSubscriptionsQuery = z.infer<
	typeof AdminSubscriptionsQuerySchema
>;

/**
 * Clearing a manual override and letting Stripe win again.
 *
 * The counterpart to `AdminSubscriptionOverrideSchema`: an override marks the
 * subscription and the webhook then refuses to overwrite entitlement, so there
 * has to be one deliberate action that hands authority back. `cancelOverride`
 * exists separately from a plan re-grant because the common case is "the manual
 * grant was a stopgap, the customer has now actually paid".
 */
export const AdminSubscriptionReconcileSchema = z
	.object({
		reason: z.string().trim().min(1).max(500),
	})
	.describe("Re-sync a subscription with Stripe and clear any manual override");

export type AdminSubscriptionReconcile = z.infer<
	typeof AdminSubscriptionReconcileSchema
>;

// ============ Audit log ============

export const AdminAuditActionSchema = z.enum(ADMIN_AUDIT_ACTIONS);

export const AdminAuditQuerySchema = AdminListQuerySchema.extend({
	sortBy: z.enum(["createdAt"]).default("createdAt"),
	action: AdminAuditActionSchema.optional(),
	actorId: z.string().optional(),
	targetType: z.enum(ADMIN_AUDIT_TARGET_TYPES).optional(),
	targetId: z.string().optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
}).describe("Audit log query");

export type AdminAuditQuery = z.infer<typeof AdminAuditQuerySchema>;

export const AdminAuditEntrySchema = z
	.object({
		id: z.string(),
		actorId: z.string(),
		actorEmail: z.string(),
		action: AdminAuditActionSchema,
		targetType: z.string(),
		targetId: z.string(),
		before: z.unknown().nullable(),
		after: z.unknown().nullable(),
		ip: z.string().nullable(),
		userAgent: z.string().nullable(),
		createdAt: z.string(),
	})
	.describe("One operator action");

export type AdminAuditEntry = z.infer<typeof AdminAuditEntrySchema>;

// ============ Activity events ============

export const ActivityEventActionSchema = z.enum(ACTIVITY_EVENT_ACTIONS);

export const ActivityQuerySchema = AdminListQuerySchema.extend({
	sortBy: z.enum(["createdAt"]).default("createdAt"),
	action: ActivityEventActionSchema.optional(),
	organizationId: z.string().optional(),
	actorUserId: z.string().optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
}).describe("Tenant activity query");

export type ActivityQuery = z.infer<typeof ActivityQuerySchema>;

export const ActivityEventSchema = z
	.object({
		id: z.string(),
		createdAt: z.string(),
		organizationId: z.string().nullable(),
		actorUserId: z.string().nullable(),
		actorType: z.enum(ACTIVITY_ACTOR_TYPES),
		/** Denormalised on purpose: the label as of the event, not a live join. */
		actorLabel: z.string().nullable(),
		action: ActivityEventActionSchema,
		targetType: z.string().nullable(),
		targetId: z.string().nullable(),
		metadata: z.record(z.string(), z.unknown()).nullable(),
		/** Nulled once past `ipRetentionDays`; the event itself survives. */
		ip: z.string().nullable(),
		userAgent: z.string().nullable(),
	})
	.describe("One tenant-side event");

export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

// ============ Cross-tenant operations ============

/**
 * Every operations list is organization-optional: the same endpoint answers
 * "show me everything" for a sweep and "show me this tenant" for the org detail
 * hub, so the console does not need two hooks per resource.
 */
const AdminOrgScopedQuerySchema = AdminListQuerySchema.extend({
	organizationId: z.string().trim().max(100).optional(),
});

export const AdminProfilesQuerySchema = AdminOrgScopedQuerySchema.extend({
	sortBy: z
		.enum(["createdAt", "name", "lastUsedAt", "launchCount"])
		.default("createdAt"),
	status: z.enum(["idle", "running", "deleted"]).optional(),
	/** Soft-deleted profiles stay in the table for session history. */
	includeDeleted: flag(false),
}).describe("Cross-tenant profile search");

export type AdminProfilesQuery = z.infer<typeof AdminProfilesQuerySchema>;

export const AdminProfileSessionsQuerySchema = AdminOrgScopedQuerySchema.extend(
	{
		sortBy: z
			.enum(["startedAt", "endedAt", "heartbeatAt"])
			.default("startedAt"),
		status: z.enum(["running", "stopped", "crashed"]).optional(),
		profileId: z.string().trim().max(100).optional(),
		/**
		 * Running rows whose worker has not reported liveness inside the reaper's
		 * deadline, which is the shape of "stuck" support is asked about.
		 */
		stuckOnly: flag(false),
	},
).describe("Cross-tenant browser session search");

export type AdminProfileSessionsQuery = z.infer<
	typeof AdminProfileSessionsQuerySchema
>;

export const AdminLicenseSlotsQuerySchema = AdminOrgScopedQuerySchema.extend({
	sortBy: z.enum(["createdAt", "expiresAt"]).default("createdAt"),
	/** Reservations still holding concurrency: unconsumed and unexpired. */
	liveOnly: flag(false),
}).describe("Launch reservation search");

export type AdminLicenseSlotsQuery = z.infer<
	typeof AdminLicenseSlotsQuerySchema
>;

export const AdminApiKeysQuerySchema = AdminOrgScopedQuerySchema.extend({
	sortBy: z.enum(["createdAt", "name", "lastRequest"]).default("createdAt"),
	enabled: optionalFlag(),
}).describe("Cross-tenant API key search");

export type AdminApiKeysQuery = z.infer<typeof AdminApiKeysQuerySchema>;

export const AdminUsageQuerySchema = AdminOrgScopedQuerySchema.extend({
	sortBy: z
		.enum(["periodStart", "sessionsLaunched", "peakConcurrentSessions"])
		.default("periodStart"),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
}).describe("Cross-tenant usage metrics query");

export type AdminUsageQuery = z.infer<typeof AdminUsageQuerySchema>;

// ============ Platform settings ============

export const SignupEmailPolicySchema = z
	.object({
		mode: z.enum(["off", "allow", "deny"]),
		domains: z.array(z.string().trim().min(1).max(253)),
	})
	.describe("Domain allow/deny list applied at signup");

export type SignupEmailPolicy = z.infer<typeof SignupEmailPolicySchema>;

/**
 * Every `PlatformSetting` key with its value type. One object rather than a
 * key/value union so `PlatformSettings["signupEnabled"]` is `boolean` and
 * nothing has to cast a JSON column at the call site.
 */
export const PlatformSettingsSchema = z
	.object({
		signupEnabled: z.boolean(),
		signinEnabled: z.boolean(),
		googleOauthEnabled: z.boolean(),
		githubOauthEnabled: z.boolean(),
		requireEmailVerification: z.boolean(),
		newOrganizationsEnabled: z.boolean(),
		defaultPlanSlug: z.string().trim().min(1).max(100),
		signupEmailPolicy: SignupEmailPolicySchema,
		maintenanceMode: z.boolean(),
		maintenanceMessage: z.string().max(2000),
		activityRetentionDays: z.number().int().min(1).max(3650),
		/** 0 disables IP capture entirely rather than retaining forever. */
		ipRetentionDays: z.number().int().min(0).max(3650),
		profileSessionRetentionDays: z.number().int().min(1).max(3650),
	})
	.describe("Platform settings, all keys present");

export type PlatformSettings = z.infer<typeof PlatformSettingsSchema>;

export type PlatformSettingKey = keyof PlatformSettings;

export type PlatformSettingValue<K extends PlatformSettingKey> =
	PlatformSettings[K];

export const PLATFORM_SETTING_KEYS = Object.keys(
	PlatformSettingsSchema.shape,
) as PlatformSettingKey[];

/**
 * Fallbacks for a key that has no row yet, so introducing the table changes no
 * behaviour.
 *
 * Two of these cannot be fully expressed as a constant and are resolved by the
 * accessor in `apps/saas-api`, which is the only place with the environment to
 * hand: `requireEmailVerification` is `NODE_ENV === "production"` today, and
 * the OAuth toggles are derived from whether a client secret is configured. The
 * values here are the production defaults, so reading this constant in a
 * browser is never wrong about production, only about a dev machine.
 */
export const PLATFORM_SETTING_DEFAULTS: PlatformSettings = {
	signupEnabled: true,
	signinEnabled: true,
	googleOauthEnabled: true,
	githubOauthEnabled: true,
	requireEmailVerification: true,
	newOrganizationsEnabled: true,
	defaultPlanSlug: "free",
	signupEmailPolicy: { mode: "off", domains: [] },
	maintenanceMode: false,
	maintenanceMessage: "",
	activityRetentionDays: 365,
	ipRetentionDays: 30,
	profileSessionRetentionDays: 90,
};

/**
 * A settings write. Partial because the settings screen saves one section at a
 * time; the server merges over whatever is stored.
 */
export const PlatformSettingsUpdateSchema = PlatformSettingsSchema.partial()
	.refine((value) => Object.keys(value).length > 0, {
		message: "At least one setting is required",
	})
	.describe("Patch platform settings");

export type PlatformSettingsUpdate = z.infer<
	typeof PlatformSettingsUpdateSchema
>;

/**
 * The subset `apps/web` is allowed to read unauthenticated, so the customer app
 * can hide the signup form when signup is off rather than letting someone fill
 * it in and submit into a rejection. Nothing here is operator-sensitive.
 */
export const PublicPlatformSettingsSchema = PlatformSettingsSchema.pick({
	signupEnabled: true,
	signinEnabled: true,
	googleOauthEnabled: true,
	githubOauthEnabled: true,
	maintenanceMode: true,
	maintenanceMessage: true,
}).describe("Platform settings safe to expose to customers");

export type PublicPlatformSettings = z.infer<
	typeof PublicPlatformSettingsSchema
>;
